import { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "../../lib/api";
import { loadPlatformFavicon } from "../../lib/favicon";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Image as ImageIcon, Upload, Trash2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

export default function Branding() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cacheBust, setCacheBust] = useState(Date.now());
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/branding");
      setInfo(data);
      setCacheBust(Date.now());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/favicon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Favicon aggiornata e applicata");
      await load();
      await loadPlatformFavicon();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeFavicon = async () => {
    if (!window.confirm("Rimuovere la favicon personalizzata e ripristinare quella predefinita?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/branding/favicon");
      toast.success("Favicon rimossa");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const faviconUrl = info?.has_favicon ? `${API}/branding/favicon?v=${cacheBust}` : null;

  return (
    <div data-testid="admin-branding">
      <PageHeader
        title="Favicon & Icone"
        subtitle="Carica la favicon del sito (l'icona mostrata nella scheda del browser). Verrà applicata automaticamente all'app."
        icon={ImageIcon}
      />

      <div className="max-w-2xl rounded-xl border border-slate-800 bg-[#121824] p-6">
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : (
          <>
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 rounded-lg border border-slate-700 bg-slate-900/60 flex items-center justify-center overflow-hidden shrink-0" data-testid="favicon-preview">
                {faviconUrl ? (
                  <img src={faviconUrl} alt="Favicon attuale" className="h-16 w-16 object-contain" data-testid="favicon-preview-img" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-slate-600" />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-100" data-testid="favicon-status">
                  {info?.has_favicon ? "Favicon personalizzata attiva" : "Nessuna favicon personalizzata"}
                </div>
                <div className="text-xs text-slate-400 mt-0.5 truncate">
                  {info?.has_favicon ? (info.filename || "favicon") : "È in uso l'icona predefinita dell'app."}
                </div>
                {info?.has_favicon && info?.updated_at && (
                  <div className="text-[11px] text-slate-500 mt-0.5">Aggiornata il {new Date(info.updated_at).toLocaleString("it-IT")}</div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-6">
              <Button onClick={() => fileInput.current?.click()} disabled={busy} data-testid="favicon-upload-btn"
                className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {info?.has_favicon ? "Sostituisci favicon" : "Carica favicon"}
              </Button>
              {info?.has_favicon && (
                <Button onClick={removeFavicon} disabled={busy} variant="outline" data-testid="favicon-remove-btn"
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="h-4 w-4 mr-2" /> Rimuovi
                </Button>
              )}
            </div>

            <div className="flex items-start gap-2 mt-5 text-xs text-slate-400 border-t border-slate-800 pt-4">
              <Info className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
              <p>Formati supportati: PNG, ICO, SVG, JPG, WEBP, GIF (max 2MB). Per una resa ottimale usa un'immagine quadrata di almeno 32×32 px. La nuova icona compare nella scheda del browser; potrebbe essere necessario ricaricare la pagina.</p>
            </div>

            <input ref={fileInput} type="file" accept=".png,.ico,.svg,.jpg,.jpeg,.webp,.gif,image/*" className="hidden" onChange={onPick} data-testid="favicon-file-input" />
          </>
        )}
      </div>
    </div>
  );
}
